import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Drawer from './Drawer';
import Header from './Header';
import HorizontalBar from './Drawer/HorizontalBar';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import { PageHeaderProvider } from 'contexts/PageHeaderContext';
import AuthGuard from 'utils/route-guard/AuthGuard';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

import AppErrorBoundary from 'components/AppErrorBoundary';

// ==============================|| MAIN LAYOUT ||============================== //

export default function DashboardLayout() {
  const { pathname } = useLocation();
  const isInspectionEditor = pathname.startsWith('/ppf/inspections/editor');
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const { container, miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return; // skip forcing drawer state on initial load — respect persisted value
    }
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
  }, [downXL, miniDrawer]);

  if (menuMasterLoading) return <Loader />;

  return (
    <AuthGuard>
      <PageHeaderProvider>
        <Box sx={{ display: 'flex', width: '100%' }}>
          <Header />
          {!isHorizontal ? <Drawer /> : <HorizontalBar />}

          <Box component="main" sx={{ width: 'calc(100% - 270px)', flexGrow: 1, p: isInspectionEditor ? { xs: 1, sm: 1.5 } : { xs: 2, sm: 3 } }}>
            <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
            <Container
              maxWidth={container ? 'xl' : false}
              sx={{
                ...(container && { px: { xs: 0, sm: 2 } }),
                position: 'relative',
                minHeight: 'calc(100vh - 130px)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {!isInspectionEditor && !pathname.includes('/contractors/masteragreement') && pathname !== '#!' && <Breadcrumbs />}
              <AppErrorBoundary>
                <Outlet />
              </AppErrorBoundary>
            </Container>
          </Box>
        </Box>
      </PageHeaderProvider>
    </AuthGuard>
  );
}
